import { useState, useEffect, useCallback } from 'react';
import { MESSAGE_TYPE, useFloatMessage } from './../../../Providers/FloatMessage';

const STORAGE_KEY_ANECDOTES_QUEUE = 'anecdotes_queue_v1';

export const INTERVAL_PRESETS = [
    { label: '10s', value: 10000 },
    { label: '15s', value: 15000 },
    { label: '20s', value: 20000 },
    { label: '30s', value: 30000 },
    { label: '45s', value: 45000 },
    { label: '60s', value: 60000 }
];

const UseAnecdotePlayer = (actions) => {
    const { showMessage } = useFloatMessage();

    // --- ESTADOS ---
    const [isLive, setIsLive] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [autoPlayInterval, setAutoPlayInterval] = useState(15000); // 15 seg default
    const [displayMode, setDisplayMode] = useState('fullscreen'); // 'fullscreen' | 'cintillo'
    const [projectionStatus, setProjectionStatus] = useState("Fuera del aire");

    // Fila persistida
    const [queue, setQueue] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_ANECDOTES_QUEUE);
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    const [currentIndex, setCurrentIndex] = useState(0);

    // Persistencia
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_ANECDOTES_QUEUE, JSON.stringify(queue));
        } catch (err) {
            console.error("Error al guardar fila de anécdotas en localStorage:", err);
        }
    }, [queue]);

    // --- GESTIÓN DE FILA ---
    const addToQueue = useCallback((item) => {
        const queueItem = { ...item, _qid: `${Date.now()}_${Math.random()}` };
        setQueue(prev => [...prev, queueItem]);
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Anécdota #${item.id} agregada a la fila.` });
    }, [showMessage]);

    const removeFromQueue = useCallback((index) => {
        setQueue(prev => {
            const newQ = prev.filter((_, i) => i !== index);
            if (index < currentIndex) {
                setCurrentIndex(c => Math.max(0, c - 1));
            } else if (index === currentIndex) {
                setIsPlaying(false);
                if (newQ.length === 0) {
                    setCurrentIndex(0);
                } else if (currentIndex >= newQ.length) {
                    setCurrentIndex(newQ.length - 1);
                }
            }
            return newQ;
        });
    }, [currentIndex]);

    const clearQueue = useCallback(() => {
        setQueue([]);
        setCurrentIndex(0);
        setIsPlaying(false);
        setProjectionStatus(isLive ? "En vivo (Fila vacía)" : "Fuera del aire");
    }, [isLive]);

    const reorderQueue = useCallback((fromIdx, toIdx) => {
        if (toIdx < 0 || toIdx >= queue.length) return;
        setQueue(prev => {
            const list = [...prev];
            const [moved] = list.splice(fromIdx, 1);
            list.splice(toIdx, 0, moved);
            if (currentIndex === fromIdx) setCurrentIndex(toIdx);
            else if (currentIndex === toIdx) setCurrentIndex(fromIdx);
            return list;
        });
    }, [queue.length, currentIndex]);

    // --- PROYECCIÓN ---
    const projectCurrent = useCallback(async (idx, mode = displayMode) => {
        const item = queue[idx];
        if (!item) return;

        setProjectionStatus(`Proyectando: #${item.id} - ${item.sender || 'Anónimo'}`);
        try {
            if (actions && typeof actions.sendToScreen === 'function') {
                await actions.sendToScreen(item, mode);
            }
        } catch (error) {
            console.error("Error en reproducción:", error);
            setIsPlaying(false);
        }
    }, [queue, displayMode, actions]);

    // --- CONTROLES DE REPRODUCCIÓN ---
    const toggleLive = async () => {
        if (isLive) {
            setIsLive(false);
            setIsPlaying(false);
            setProjectionStatus("Fuera del aire");
            if (actions && typeof actions.clearScreen === 'function') {
                await actions.clearScreen();
            }
        } else {
            if (queue.length === 0) {
                showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La fila está vacía. Agrega anécdotas primero.' });
                return;
            }
            setIsLive(true);
            setProjectionStatus("En vivo (Esperando acción...)");
        }
    };

    const togglePlay = () => {
        if (!isLive) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'Debes iniciar la proyección primero.' });
            return;
        }

        if (queue.length === 0) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'La fila está vacía.' });
            return;
        }

        const newPlayingState = !isPlaying;
        setIsPlaying(newPlayingState);

        if (newPlayingState) {
            projectCurrent(currentIndex);
        }
    };

    const playNext = useCallback(() => {
        if (queue.length === 0) return;

        if (currentIndex + 1 >= queue.length) {
            setIsPlaying(false);
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Fin de la fila de anécdotas.' });
            return;
        }

        const nextIdx = currentIndex + 1;
        setCurrentIndex(nextIdx);

        if (isLive) {
            projectCurrent(nextIdx);
        }
    }, [currentIndex, queue.length, isLive, showMessage, projectCurrent]);

    const playPrev = useCallback(() => {
        if (queue.length === 0) return;

        if (currentIndex > 0) {
            const prevIdx = currentIndex - 1;
            setCurrentIndex(prevIdx);
            if (isLive) {
                projectCurrent(prevIdx);
            }
        }
    }, [currentIndex, queue.length, isLive, projectCurrent]);

    const jumpTo = (index) => {
        if (index < 0 || index >= queue.length) return;
        setCurrentIndex(index);
        if (isLive) {
            projectCurrent(index);
        }
    };

    const clearScreen = async () => {
        if (actions && typeof actions.clearScreen === 'function') {
            await actions.clearScreen();
        }
        setProjectionStatus(isLive ? "En vivo (Pantalla limpia)" : "Fuera del aire");
    };

    // Actualizar intervalo permitiendo milisegundos o segundos numéricos
    const updateAutoPlayInterval = (valueInSecondsOrMs) => {
        const num = Number(valueInSecondsOrMs);
        if (Number.isNaN(num) || num <= 0) return;
        // Si el valor recibido es menor a 1000 asumimos que viene en segundos
        const ms = num < 1000 ? num * 1000 : num;
        setAutoPlayInterval(ms);
    };

    // Temporizador para reproducción automática
    useEffect(() => {
        let intervalId;
        if (isLive && isPlaying) {
            intervalId = setInterval(() => {
                playNext();
            }, autoPlayInterval);
        }
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [isLive, isPlaying, autoPlayInterval, playNext]);

    return {
        isLive,
        isPlaying,
        queue,
        currentIndex,
        projectionStatus,
        autoPlayInterval,
        displayMode,
        setDisplayMode,
        addToQueue,
        removeFromQueue,
        clearQueue,
        reorderQueue,
        toggleLive,
        togglePlay,
        playNext,
        playPrev,
        jumpTo,
        clearScreen,
        setAutoPlayInterval: updateAutoPlayInterval
    };
};

export { UseAnecdotePlayer };
